import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import DoctorSidebar from "./DoctorSidebar";
import "./DoctorPatients.css";

const DoctorPatients = () => {
  const { doctorUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [pastAppointments, setPastAppointments] = useState([]);
  const [completedAppointments, setCompletedAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState(null);
  const [viewMode, setViewMode] = useState("upcoming");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageContent, setMessageContent] = useState("");
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notes, setNotes] = useState("");
  const [fileUpload, setFileUpload] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({ date: "", time: "", reason: "" });
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [processingIds, setProcessingIds] = useState(new Set());
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);

  const fetchPatients = async () => {
    setIsLoading(true);
    if (!doctorUser || doctorUser.role !== "doctor") {
      setError("You must be logged in as a doctor to view patients.");
      setTimeout(() => navigate('/doctor-login'), 2000);
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.get(`/appointments/doctor/${doctorUser.id}`, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      const appointments = response.data;
      const now = new Date();

      setUpcomingAppointments(
        appointments.filter((app) => {
          const effectiveDate = app.rescheduledDate ? new Date(app.rescheduledDate) : new Date(app.date);
          return effectiveDate >= now && ["accepted", "rescheduled"].includes(app.status.toLowerCase());
        })
      );

      setPastAppointments(
        appointments.filter((app) => {
          const effectiveDate = app.rescheduledDate ? new Date(app.rescheduledDate) : new Date(app.date);
          return effectiveDate < now && app.status.toLowerCase() !== "completed";
        })
      );

      setCompletedAppointments(
        appointments.filter((app) => app.status.toLowerCase() === "completed")
      );
    } catch (error) {
      setError(`Failed to load patients: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, [doctorUser, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchPatientHistory = useCallback(async (patientId) => {
    if (!doctorUser) {
      setError("No authenticated user found. Redirecting to login...");
      navigate("/doctor-login");
      return;
    }

    try {
      const response = await api.get(`/appointments/patient/${patientId}/history`, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      setPatientHistory({
        medicalHistory: response.data.medicalHistory || "No medical history available",
        prescriptions: response.data.prescriptions || []
      });
    } catch (error) {
      console.error('Failed to load patient history:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        setError("Authentication failed. Redirecting to login...");
        setTimeout(() => navigate("/doctor-login"), 1000);
      } else if (error.response?.status === 403) {
        setError("You do not have permission to access this patient's history.");
      } else {
        setError("Failed to load patient history: " + (error.response?.data?.message || error.message));
      }
      setPatientHistory(null);
    }
  }, [doctorUser, navigate]);

  const filterAppointments = useMemo(() => (appointments) => {
    return appointments.filter((app) => {
      const effectiveDate = app.rescheduledDate ? new Date(app.rescheduledDate) : new Date(app.date);
      const dateMatch = filterDate ? effectiveDate.toISOString().split("T")[0] === filterDate : true;
      const statusMatch = filterStatus ? app.status.toLowerCase() === filterStatus.toLowerCase() : true;
      const searchMatch = searchQuery
        ? (app.name.toLowerCase().includes(searchQuery.toLowerCase()) || app.patientId?.toString().toLowerCase().includes(searchQuery.toLowerCase()))
        : true;
      return dateMatch && statusMatch && searchMatch;
    });
  }, [filterDate, filterStatus, searchQuery]);

  const handleViewHistory = (appointment) => {
    const patientId = typeof appointment.patientId === 'object' 
      ? appointment.patientId?._id?.toString() 
      : appointment.patientId?.toString();
    
    if (!patientId || !/^[0-9a-fA-F]{24}$/.test(patientId)) {
      setError("Invalid patient ID in appointment data.");
      return;
    }

    if (selectedPatient && selectedPatient.patientId === patientId && selectedPatient.latestAppointmentId === appointment._id) {
      setSelectedPatient(null);
      setPatientHistory(null);
    } else {
      setSelectedPatient({ patientId, latestAppointmentId: appointment._id });
      fetchPatientHistory(patientId);
    }
  };

  const handleSendMessage = async () => {
    if (!selectedPatient || processingIds.has(selectedPatient.latestAppointmentId)) return;
    setProcessingIds((prev) => new Set(prev).add(selectedPatient.latestAppointmentId));

    try {
      await api.post("/notifications", {
        recipient: selectedPatient.patientId,
        message: `${messageContent} - This message is sent to you by Dr. ${doctorUser.name || "Unknown"}`,
        doctorId: doctorUser.id,
        appointmentId: selectedPatient.latestAppointmentId || null,
      }, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      setSuccessMessage("Message sent successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowMessageModal(false);
      setMessageContent("");
    } catch (error) {
      setError(`Failed to send message: ${error.response?.data?.message || error.message}`);
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedPatient.latestAppointmentId);
        return newSet;
      });
    }
  };

  const handleReschedule = (appointmentId) => {
    setSelectedAppointment({ _id: appointmentId });
    setShowRescheduleModal(true);
    setRescheduleData({ date: "", time: "", reason: "" });
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedAppointment || processingIds.has(selectedAppointment._id)) return;
    setProcessingIds((prev) => new Set(prev).add(selectedAppointment._id));

    const updatedApp = {
      ...upcomingAppointments.find((app) => app._id === selectedAppointment._id) || 
      pastAppointments.find((app) => app._id === selectedAppointment._id),
      status: "rescheduled",
      rescheduledDate: `${rescheduleData.date}T${rescheduleData.time}:00`,
    };
    setUpcomingAppointments((prev) => prev.filter((app) => app._id !== selectedAppointment._id));
    setPastAppointments((prev) => prev.filter((app) => app._id !== selectedAppointment._id));
    setUpcomingAppointments((prev) => [...prev, updatedApp]);

    try {
      if (!rescheduleData.date || !rescheduleData.time || !rescheduleData.reason) {
        throw new Error("Please provide all reschedule details.");
      }
      const newDateTime = `${rescheduleData.date}T${rescheduleData.time}:00`;
      await api.put(`/appointments/${selectedAppointment._id}/reschedule`, { 
        date: newDateTime,
        reason: rescheduleData.reason
      }, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
     });
      await fetchPatients();
      setSuccessMessage("Appointment successfully rescheduled!");
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowRescheduleModal(false);
      setSelectedAppointment(null);
    } catch (error) {
      setError(`Failed to reschedule appointment: ${error.response?.data?.message || error.message}`);
      fetchPatients();
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedAppointment._id);
        return newSet;
      });
    }
  };

  const handleStartConsultation = (appointmentId) => {
    navigate(`/doctor/video-call/${appointmentId}`);
  };

  const handleUploadRecords = async (patientId) => {
    if (!fileUpload || processingIds.has(patientId)) return;
    setProcessingIds((prev) => new Set(prev).add(patientId));

    try {
      const formData = new FormData();
      formData.append("file", fileUpload);
      await api.post(`/appointments/patient/${patientId}/records`, formData, {
        headers: { 
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${doctorUser.accessToken}`
        }
      });
      setSuccessMessage("Records uploaded successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
      setFileUpload(null);
      await fetchPatients();
    } catch (error) {
      setError(`Failed to upload records: ${error.response?.data?.message || error.message}`);
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(patientId);
        return newSet;
      });
    }
  };

  const handleAddNotes = (appointment) => {
    setSelectedPatient({ 
      patientId: typeof appointment.patientId === 'object' 
        ? appointment.patientId?._id?.toString() 
        : appointment.patientId?.toString(), 
      latestAppointmentId: appointment._id 
    });
    setNotes(appointment.notes || "");
    setShowNotesModal(true);
  };

  const handleNotesSubmit = async () => {
    if (!selectedPatient || processingIds.has(selectedPatient.latestAppointmentId)) return;
    setProcessingIds((prev) => new Set(prev).add(selectedPatient.latestAppointmentId));

    try {
      await api.put(`/appointments/${selectedPatient.latestAppointmentId}/notes`, { notes }, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      await fetchPatients();
      setSuccessMessage("Notes saved successfully!");
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowNotesModal(false);
      setSelectedPatient(null);
      setNotes("");
    } catch (error) {
      setError(`Failed to save notes: ${error.response?.data?.message || error.message}`);
    } finally {
      setProcessingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedPatient.latestAppointmentId);
        return newSet;
      });
    }
  };

  const handleMakePrescription = async (appointment) => {
    const patientId = typeof appointment.patientId === 'object' 
      ? appointment.patientId?._id?.toString() 
      : appointment.patientId?.toString();
    
    if (!patientId || !/^[0-9a-fA-F]{24}$/.test(patientId)) {
      setError("Invalid patient ID in appointment data.");
      return;
    }

    try {
      const response = await api.get(`/prescriptions/appointment/${appointment._id}`, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` }
      });
      
      if (response.data) {
        const confirmUpdate = window.confirm(
          "A prescription has already been created for this appointment on " + 
          new Date(response.data.createdAt).toLocaleString() + 
          ". Would you like to update it?"
        );
        if (confirmUpdate) {
          navigate(`/doctor/prescription/${appointment._id}`);
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        navigate(`/doctor/prescription/${appointment._id}`);
      } else {
        setError("Error checking prescription status: " + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleViewPrescription = async (appointmentId) => {
    try {
      const response = await api.get(`/prescriptions/appointment/${appointmentId}`, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
      });
      setSelectedPrescription(response.data);
      setShowPrescriptionModal(true);
    } catch (error) {
      if (error.response?.status === 404) {
        setError("No prescription found for this appointment. Please create one first.");
      } else if (error.response?.status === 401) {
        setError("Authentication failed. Please log in again.");
        setTimeout(() => navigate("/doctor-login"), 2000);
      } else {
        setError("Failed to load prescription: " + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleDownload = async (prescriptionData) => {
    if (!prescriptionData.pdfPath) {
      setError("No file path available for download.");
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      const response = await api.get(`/appointments/prescriptions/${prescriptionData._id}/download-direct-doctor`, {
        headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
        responseType: 'blob', // Handle binary data
      });

      const filename = `Prescription-${new Date(prescriptionData.createdAt).toLocaleDateString().replace(/\//g, '-')}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError("Failed to download prescription: " + (error.response?.data?.message || error.message));
      setTimeout(() => setError(null), 3000);
      console.error("Download error:", error);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  if (isLoading) {
    return (
      <div className="doctor-patients-container">
        <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="patients-content">
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            {sidebarOpen ? "✕" : "☰"}
          </button>
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Loading your TureMedicine patients...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="doctor-patients-container">
        <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <div className="patients-content">
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            {sidebarOpen ? "✕" : "☰"}
          </button>
          <div className="error-message">
            {error}
            <button onClick={() => { setError(null); fetchPatients(); }}>Retry</button>
            <button onClick={() => navigate("/doctor-login")}>Go to Login</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="doctor-patients-container">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="patients-content">
        <button className="sidebar-toggle" onClick={toggleSidebar}>
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <h1>Patients</h1>
        {successMessage && <div className="success-message">{successMessage}</div>}

        <div className="filter-search-section">
          <div className="filter-group">
            <label htmlFor="filterDate">Filter by Date:</label>
            <input type="date" id="filterDate" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} />
          </div>
          <div className="filter-group">
            <label htmlFor="filterStatus">Filter by Status:</label>
            <select id="filterStatus" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rescheduled">Rescheduled</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="searchQuery">Search:</label>
            <input
              type="text"
              id="searchQuery"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name or ID"
            />
          </div>
        </div>

        <div className="view-toggle">
          <button className={viewMode === "upcoming" ? "active" : ""} onClick={() => setViewMode("upcoming")}>
            Upcoming Appointments
          </button>
          <button className={viewMode === "past" ? "active" : ""} onClick={() => setViewMode("past")}>
            Past Appointments
          </button>
          <button className={viewMode === "completed" ? "active" : ""} onClick={() => setViewMode("completed")}>
            Completed Appointments
          </button>
        </div>

        <div className="patients-list">
          {viewMode === "upcoming" && filterAppointments(upcomingAppointments).length > 0 ? (
            filterAppointments(upcomingAppointments).map((app) => (
              <div key={app._id} className="patient-card">
                <div className="patient-info">
                  <p><strong>Name:</strong> {app.name}</p>
                  <p><strong>Age:</strong> {app.age}</p>
                  <p><strong>Gender:</strong> {app.gender}</p>
                  <p><strong>Original Appointment Date:</strong> {new Date(app.date).toLocaleString()}</p>
                  {app.status.toLowerCase() === "rescheduled" && app.rescheduledDate && (
                    <p><strong>Rescheduled Date:</strong> {new Date(app.rescheduledDate).toLocaleString()}</p>
                  )}
                  <p><strong>Status:</strong> {app.status}</p>
                  {app.notes && <p><strong>Notes:</strong> {app.notes}</p>}
                </div>
                <div className="patient-actions">
                  <button onClick={() => handleViewHistory(app)}>
                    {selectedPatient && selectedPatient.latestAppointmentId === app._id ? "Hide History" : "View History"}
                  </button>
                  <button onClick={() => handleStartConsultation(app._id)}>Start Consultation</button>
                  <button 
                    onClick={() => handleReschedule(app._id)} 
                    disabled={processingIds.has(app._id)}
                  >
                    {processingIds.has(app._id) ? "Processing..." : "Reschedule"}
                  </button>
                  <button 
                    onClick={() => { setSelectedPatient({ patientId: typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString(), latestAppointmentId: app._id }); setShowMessageModal(true); }}
                    disabled={processingIds.has(app._id)}
                  >
                    {processingIds.has(app._id) ? "Processing..." : "Send Message"}
                  </button>
                  <button 
                    onClick={() => handleAddNotes(app)} 
                    disabled={processingIds.has(app._id)}
                  >
                    {processingIds.has(app._id) ? "Processing..." : "Add/Edit Notes"}
                  </button>
                  <input type="file" onChange={(e) => setFileUpload(e.target.files[0])} />
                  <button 
                    onClick={() => handleUploadRecords(typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString())}
                    disabled={processingIds.has(typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString())}
                  >
                    {processingIds.has(typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString()) ? "Processing..." : "Upload Records"}
                  </button>
                </div>
                {selectedPatient && selectedPatient.latestAppointmentId === app._id && (
                  <div className="patient-history">
                    <h3>Medical History & Prescriptions</h3>
                    {patientHistory ? (
                      <>
                        <div className="history-section">
                          <h4>Medical History</h4>
                          <p>{patientHistory.medicalHistory || "No medical history available."}</p>
                        </div>
                        <div className="prescriptions-section">
                          <h4>Previous Prescriptions</h4>
                          {patientHistory.prescriptions && patientHistory.prescriptions.length > 0 ? (
                            <ul>
                              {patientHistory.prescriptions.map((prescription, presIndex) => (
                                <li key={presIndex}>
                                  {prescription.medications.map((med, medIndex) => (
                                    <span key={medIndex}>
                                      {med.name} - {med.dosage} ({med.frequency}, {med.duration})
                                      {medIndex < prescription.medications.length - 1 ? ", " : ""}
                                    </span>
                                  ))}
                                  {" - Issued on: " + new Date(prescription.date).toLocaleString()}
                                  {prescription.instructions && ` - Instructions: ${prescription.instructions}`}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p>No previous prescriptions available.</p>
                          )}
                        </div>
                      </>
                    ) : (
                      <p>Loading history...</p>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : viewMode === "upcoming" ? (
            <p>No upcoming appointments found.</p>
          ) : null}

          {viewMode === "past" && filterAppointments(pastAppointments).length > 0 ? (
            filterAppointments(pastAppointments).map((app) => (
              <div key={app._id} className="patient-card">
                <div className="patient-info">
                  <p><strong>Name:</strong> {app.name}</p>
                  <p><strong>Age:</strong> {app.age}</p>
                  <p><strong>Gender:</strong> {app.gender}</p>
                  <p><strong>Original Appointment Date:</strong> {new Date(app.date).toLocaleString()}</p>
                  {app.status.toLowerCase() === "rescheduled" && app.rescheduledDate && (
                    <p><strong>Rescheduled Date:</strong> {new Date(app.rescheduledDate).toLocaleString()}</p>
                  )}
                  <p><strong>Status:</strong> {app.status}</p>
                  {app.notes && <p><strong>Notes:</strong> {app.notes}</p>}
                </div>
                <div className="patient-actions">
                  <button onClick={() => handleViewHistory(app)}>
                    {selectedPatient && selectedPatient.latestAppointmentId === app._id ? "Hide History" : "View History"}
                  </button>
                  <button onClick={() => handleStartConsultation(app._id)}>Start Consultation</button>
                  <button 
                    onClick={() => handleReschedule(app._id)} 
                    disabled={processingIds.has(app._id)}
                  >
                    {processingIds.has(app._id) ? "Processing..." : "Reschedule"}
                  </button>
                  <button 
                    onClick={() => { setSelectedPatient({ patientId: typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString(), latestAppointmentId: app._id }); setShowMessageModal(true); }}
                    disabled={processingIds.has(app._id)}
                  >
                    {processingIds.has(app._id) ? "Processing..." : "Send Message"}
                  </button>
                  <button 
                    onClick={() => handleAddNotes(app)} 
                    disabled={processingIds.has(app._id)}
                  >
                    {processingIds.has(app._id) ? "Processing..." : "Add/Edit Notes"}
                  </button>
                  <input type="file" onChange={(e) => setFileUpload(e.target.files[0])} />
                  <button 
                    onClick={() => handleUploadRecords(typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString())}
                    disabled={processingIds.has(typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString())}
                  >
                    {processingIds.has(typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString()) ? "Processing..." : "Upload Records"}
                  </button>
                </div>
                {selectedPatient && selectedPatient.latestAppointmentId === app._id && (
                  <div className="patient-history">
                    <h3>Medical History & Prescriptions</h3>
                    {patientHistory ? (
                      <>
                        <div className="history-section">
                          <h4>Medical History</h4>
                          <p>{patientHistory.medicalHistory || "No medical history available."}</p>
                        </div>
                        <div className="prescriptions-section">
                          <h4>Previous Prescriptions</h4>
                          {patientHistory.prescriptions && patientHistory.prescriptions.length > 0 ? (
                            <ul>
                              {patientHistory.prescriptions.map((prescription, presIndex) => (
                                <li key={presIndex}>
                                  {prescription.medications.map((med, medIndex) => (
                                    <span key={medIndex}>
                                      {med.name} - {med.dosage} ({med.frequency}, {med.duration})
                                      {medIndex < prescription.medications.length - 1 ? ", " : ""}
                                    </span>
                                  ))}
                                  {" - Issued on: " + new Date(prescription.date).toLocaleString()}
                                  {prescription.instructions && ` - Instructions: ${prescription.instructions}`}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p>No previous prescriptions available.</p>
                          )}
                        </div>
                      </>
                    ) : (
                      <p>Loading history...</p>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : viewMode === "past" ? (
            <p>No past appointments found.</p>
          ) : null}

          {viewMode === "completed" && filterAppointments(completedAppointments).length > 0 ? (
            filterAppointments(completedAppointments).map((app) => (
              <div key={app._id} className="patient-card completed-card">
                <div className="patient-info">
                  <p><strong>Name:</strong> {app.name}</p>
                  <p><strong>Age:</strong> {app.age}</p>
                  <p><strong>Gender:</strong> {app.gender}</p>
                  <p><strong>Appointment Date:</strong> {new Date(app.date).toLocaleString()}</p>
                  <p><strong>Status:</strong> {app.status}</p>
                  <p><strong>Video Call:</strong> Completed</p>
                  <p><strong>Prescription Status:</strong> {app.prescriptionStatus || "Pending"}</p>
                  <p><strong>Call Duration:</strong> {app.callDuration || "Not available"}</p>
                  <p><strong>Completed On:</strong> {app.completedAt ? new Date(app.completedAt).toLocaleString() : "Not available"}</p>
                  {app.notes && <p><strong>Notes:</strong> {app.notes}</p>}
                </div>
                <div className="patient-actions">
                  <button onClick={() => handleViewHistory(app)}>
                    {selectedPatient && selectedPatient.latestAppointmentId === app._id ? "Hide History" : "View History"}
                  </button>
                  {app.prescriptionStatus === "Pending" || !app.hasPrescription ? (
                    <button onClick={() => handleMakePrescription(app)}>Make Prescription</button>
                  ) : (
                    <button onClick={() => handleViewPrescription(app._id)}>View Prescription</button>
                  )}
                  <button 
                    onClick={() => { 
                      setSelectedPatient({ 
                        patientId: typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString(), 
                        latestAppointmentId: app._id 
                      }); 
                      setShowMessageModal(true); 
                    }}
                    disabled={processingIds.has(app._id)}
                  >
                    {processingIds.has(app._id) ? "Processing..." : "Send Message"}
                  </button>
                  <button 
                    onClick={() => handleAddNotes(app)} 
                    disabled={processingIds.has(app._id)}
                  >
                    {processingIds.has(app._id) ? "Processing..." : "Add/Edit Notes"}
                  </button>
                  <input type="file" onChange={(e) => setFileUpload(e.target.files[0])} />
                  <button 
                    onClick={() => handleUploadRecords(typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString())}
                    disabled={processingIds.has(typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString())}
                  >
                    {processingIds.has(typeof app.patientId === 'object' ? app.patientId?._id?.toString() : app.patientId?.toString()) ? "Processing..." : "Upload Records"}
                  </button>
                </div>
                {selectedPatient && selectedPatient.latestAppointmentId === app._id && (
                  <div className="patient-history">
                    <h3>Medical History & Prescriptions</h3>
                    {patientHistory ? (
                      <>
                        <div className="history-section">
                          <h4>Medical History</h4>
                          <p>{patientHistory.medicalHistory || "No medical history available."}</p>
                        </div>
                        <div className="prescriptions-section">
                          <h4>Previous Prescriptions</h4>
                          {patientHistory.prescriptions && patientHistory.prescriptions.length > 0 ? (
                            <ul>
                              {patientHistory.prescriptions.map((prescription, presIndex) => (
                                <li key={presIndex}>
                                  {prescription.medications.map((med, medIndex) => (
                                    <span key={medIndex}>
                                      {med.name} - {med.dosage} ({med.frequency}, {med.duration})
                                      {medIndex < prescription.medications.length - 1 ? ", " : ""}
                                    </span>
                                  ))}
                                  {" - Issued on: " + new Date(prescription.date).toLocaleString()}
                                  {prescription.instructions && ` - Instructions: ${prescription.instructions}`}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p>No previous prescriptions available.</p>
                          )}
                        </div>
                      </>
                    ) : (
                      <p>Loading history...</p>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : viewMode === "completed" ? (
            <p>No completed appointments found.</p>
          ) : null}
        </div>

        {showMessageModal && (
          <div className="modal">
            <div className="modal-content">
              <h3>Send Message</h3>
              <textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                rows="4"
                placeholder="Type your message here..."
              />
              <div className="modal-actions">
                <button 
                  onClick={handleSendMessage} 
                  disabled={processingIds.has(selectedPatient?.latestAppointmentId)}
                >
                  {processingIds.has(selectedPatient?.latestAppointmentId) ? "Processing..." : "Send"}
                </button>
                <button onClick={() => setShowMessageModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {showNotesModal && (
          <div className="modal">
            <div className="modal-content">
              <h3>Add/Edit Notes</h3>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows="5"
                placeholder="Enter patient notes..."
              />
              <div className="modal-actions">
                <button 
                  onClick={handleNotesSubmit} 
                  disabled={processingIds.has(selectedPatient?.latestAppointmentId)}
                >
                  {processingIds.has(selectedPatient?.latestAppointmentId) ? "Processing..." : "Save"}
                </button>
                <button onClick={() => setShowNotesModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {showRescheduleModal && (
          <div className="modal">
            <div className="modal-content">
              <h3>Reschedule Appointment</h3>
              <div className="form-group">
                <label>New Date:</label>
                <input
                  type="date"
                  value={rescheduleData.date}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, date: e.target.value })}
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
              <div className="form-group">
                <label>New Time:</label>
                <input
                  type="time"
                  value={rescheduleData.time}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, time: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Reason:</label>
                <select
                  value={rescheduleData.reason}
                  onChange={(e) => setRescheduleData({ ...rescheduleData, reason: e.target.value })}
                >
                  <option value="">Select Reason</option>
                  <option value="Doctor Unavailable">Doctor Unavailable</option>
                  <option value="Emergency">Emergency</option>
                  <option value="Patient Request">Patient Request</option>
                </select>
              </div>
              <div className="modal-actions">
                <button 
                  onClick={handleRescheduleSubmit} 
                  disabled={processingIds.has(selectedAppointment?._id)}
                >
                  {processingIds.has(selectedAppointment?._id) ? "Processing..." : "Confirm"}
                </button>
                <button onClick={() => setShowRescheduleModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {showPrescriptionModal && selectedPrescription && (
          <div className="modal">
            <div className="modal-content">
              <h3>Prescription Details</h3>
              <div className="prescription-details">
                <p><strong>Patient:</strong> {selectedPrescription.patientId.name}</p>
                <p><strong>Doctor:</strong> {selectedPrescription.doctorId.name}</p>
                <p><strong>Date:</strong> {new Date(selectedPrescription.createdAt).toLocaleString()}</p>
                <h4>Medications:</h4>
                <ul>
                  {selectedPrescription.medications.map((med, index) => (
                    <li key={index}>
                      {med.name} - {med.dosage} ({med.frequency}, {med.duration})
                    </li>
                  ))}
                </ul>
                {selectedPrescription.instructions && (
                  <p><strong>Instructions:</strong> {selectedPrescription.instructions}</p>
                )}
                {selectedPrescription.nextAppointmentDate && (
                  <p><strong>Next Appointment:</strong> {new Date(selectedPrescription.nextAppointmentDate).toLocaleString()}</p>
                )}
                {selectedPrescription.pdfPath && (
                  <div>
                    <button 
                      onClick={() => handleDownload(selectedPrescription)}
                      aria-label="Download prescription PDF"
                    >
                      Download Prescription PDF
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button onClick={() => navigate(`/doctor/prescription/${selectedPrescription.appointmentId}`)}>
                  Update Prescription
                </button>
                <button onClick={() => {
                  setShowPrescriptionModal(false);
                  setSelectedPrescription(null);
                }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorPatients;